import { useGoalById, useUpdateGoal } from 'features/goals';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { FilterDocument } from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { TaskGroupBy } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';

import { ActionPlanContextMenu } from '../action-plan-context-menu';
import * as Styled from './sub-items-section.style';

type InteractionsProps = {
  filterDocument: FilterDocument;
  defaultFilterDocument: FilterDocument;
  updateFilterDocument: (filterDocument: FilterDocument) => void;
  onReset: () => void;
  goals: Goal[];
  tags: Tag[];
  lifeAreas: LifeArea[];
  supportedGroupByModes: TaskGroupBy[];
  hideNoGoal?: boolean;
};

export const Interactions: React.FC<InteractionsProps> = ({
  filterDocument,
  defaultFilterDocument,
  updateFilterDocument,
  onReset,
  goals,
  tags,
  lifeAreas,
  supportedGroupByModes,
  hideNoGoal,
}) => {
  const actionContextButtonRef = useRef<HTMLButtonElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { submit: updateGoal } = useUpdateGoal();

  const { id } = useParams();
  const goal = useGoalById(id);

  const {
    menuOpen: actionContextOpen,
    openMenu: openActionContext,
    closeMenu: closeActionContext,
  } = useOpenMenu();

  const mainParentIds = useMemo(
    () => [...(goal?.parentIds ?? []), goal?.id!],
    [goal],
  );

  return (
    <Styled.InteractionsContainer>
      <IconButton
        icon={Plus}
        ref={actionContextButtonRef}
        onClick={openActionContext}
        size={Sizes.Medium}
        color={Colors.Default}
      />

      <FilterButton
        filterDocument={filterDocument}
        defaultFilterDocument={defaultFilterDocument}
        updateFilterDocument={updateFilterDocument}
        onReset={onReset}
        goals={goals}
        tags={tags}
        lifeAreas={lifeAreas}
        supportedGroupByModes={supportedGroupByModes}
        hideNoGoal={hideNoGoal}
        renderAsIconButton
      />

      <IconButton
        icon={SettingsSort18}
        onClick={() => setIsSettingsMenuOpen(true)}
        ref={settingsButtonRef}
        color={Colors.Default}
      />
      {isSettingsMenuOpen && (
        <PopupMenu
          referenceElement={settingsButtonRef}
          onOutside={() => setIsSettingsMenuOpen(false)}
          position="bottom-end"
        >
          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuToggle
                label={t('general.labels.tasks.show-completed-tasks')}
                checked={!(goal?.hideCompletedTasks ?? true)}
                onChange={(checked) => {
                  updateGoal({
                    id: goal!.id,
                    hideCompletedTasks: !checked,
                  });
                }}
              />
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}

      {actionContextOpen && (
        <ActionPlanContextMenu
          referenceElement={actionContextButtonRef}
          onClose={closeActionContext}
          id={id}
          parentIds={mainParentIds}
          lifeAreaId={goal?.lifeAreaId}
        />
      )}
    </Styled.InteractionsContainer>
  );
};
